import "./RiskMatrix.scss";
import { KSelect, KValidatableInput } from "kinnect-general";
import {
	VCol,
	VHover,
	VRow,
	VListItemContent,
	VListItemSubtitle,
	VListItemTitle
} from "vuetify/lib";

export default {
	name: "SafetyRiskMatrix",

	extends: KValidatableInput,

	props: {
		probability: Number,
		required: Boolean,
		severity: Number
	},

	data: () => ( {
		hoveredSeverity: null,
		hoveredProbability: null,
		severities: [
			{
				title: "Insignificant (1)",
				description: "No injuries / minimal financial loss",
				risk: 1
			},
			{
				title: "Minor (2)",
				description: "First aid treatment / medium financial loss",
				risk: 2
			},
			{
				title: "Significant (3)",
				description: "Medical treatment / high financial loss",
				risk: 3
			},
			{
				title: "Major (4)",
				description: "Hospitable / large financial loss",
				risk: 4
			},
			{
				title: "Catastrophic (5)",
				description: "Death / massive financial loss",
				risk: 5
			}
		],

		probabilities: [
			{
				title: "Almost Certain (5)",
				description: "Once a week",
				risk: 5
			},
			{
				title: "Likely (4)",
				description: "Once a month",
				risk: 4
			},
			{
				title: "Moderate (3)",
				description: "Once a year",
				risk: 3
			},
			{
				title: "Unlikely (2)",
				description: "Once in 10 years",
				risk: 2
			},
			{
				title: "Rare (1)",
				description: "Once in 100 years",
				risk: 1
			}
		],

		risks: {
			low: {
				action: "Implement practicable short-medium term control measures.",
				classes: "green darken-1 white--text",
				label: "Low",
				rangeMin: 1,
				rangeMax: 4
			},

			moderate: {
				action: "Corrective action needed. Action in short-medium term.",
				classes: "yellow darken-1 black--text",
				label: "Moderate",
				rangeMin: 5,
				rangeMax: 11
			},

			high: {
				action: "Corrective action needed. Action in short term.",
				classes: "orange darken-1 white--text",
				label: "High",
				rangeMin: 12,
				rangeMax: 16
			},

			extreme: {
				action: "Discontinue operation and/or immediate corrective action required.",
				classes: "red darken-1 white--text",
				label: "Extreme",
				rangeMin: 17,
				rangeMax: 25
			}
		},

		risksOrder: [
			"low",
			"moderate",
			"high",
			"extreme"
		]
	} ),

	computed: {
		internalProbability: {
			get() {
				return this.probability;
			},

			set( val ) {
				this.$emit( "update:probability", parseInt( val ) );
			}
		},

		internalSeverity: {
			get() {
				return this.severity;
			},

			set( val ) {
				this.$emit( "update:severity", parseInt( val ) );
			}
		},

		internalValue: {
			get() {
				if ( this.internalProbability && this.internalSeverity ) {
					return this.internalProbability * this.internalSeverity;
				}

				return null;
			},

			set( val ) {
				this.$emit( "input", val || null );
			}
		},

		headerClasses() {
			return {
				"grey darken-4": !this.hasError,
				"white--text": true,
				error: this.hasError
			};
		},

		genDesktopLayout() {
			return <VCol vModel={this.internalValue}>
				<VRow justify="end">
					<VCol cols={10} class={[
						"risk-matrix__cell text-subtitle-1",
						"text-subtitle-1",
						this.headerClasses
					]}>
						Severity
					</VCol>
				</VRow>

				<VRow>
					<VCol cols={2} align="end" class={[
						"risk-matrix__cell d-flex text-subtitle-1",
						this.headerClasses
					]}>
						Probability
					</VCol>

					{this.severities.map( severity =>
						<VCol
							cols={2}
							class={{
								"risk-matrix__cell grey": true,
								"darken-1 white--text": this.internalSeverity === severity.risk,
								"lighten-4": !this.isDark && this.internalSeverity !== severity.risk,
								"darken-2": this.isDark && this.internalSeverity !== severity.risk,
								"lighten-2": this.hoveredSeverity === severity.risk
							}}
						>
							<div class="text-body-2">{severity.title}</div>
							<div class="text-caption hidden-sm-and-down">{severity.description}</div>
						</VCol>
					)}
				</VRow>

				{this.probabilities.map( probability =>
					<VRow>
						<VCol
							cols={2}
							class={{
								"risk-matrix__cell grey": true,
								"darken-1 white--text": this.internalProbability === probability.risk,
								"lighten-4": !this.isDark && this.internalProbability !== probability.risk,
								"darken-2": this.isDark && this.internalProbability !== probability.risk,
								"lighten-2": this.hoveredProbability === probability.risk
							}}
						>
							<div class="text-body-2">{probability.title}</div>
							<div class="text-caption hidden-sm-and-down">{probability.description}</div>
						</VCol>

						{this.severities.map( severity =>
							<VHover
								scopedSlots={{
									default: ( { hover } ) =>
										<VCol
											class={[
												"align-center justify-center risk-matrix__cell risk-matrix__cell--actionable risk-matrix__cell--selectable d-flex text-body-2",
												this.getRiskClasses( probability.risk * severity.risk ),
												{
													"darken-2": hover,
													"risk-matrix__cell--selected": this.internalProbability === probability.risk && this.internalSeverity === severity.risk
												}
											]}
											cols={2}
											onClick={() => {
												const selected = this.internalProbability === probability.risk
													&& this.internalSeverity === severity.risk;

												if ( selected ) {
													this.internalProbability = 0;
													this.internalSeverity = 0;
												} else {
													this.internalProbability = probability.risk;
													this.internalSeverity = severity.risk;
												}
											}}
											onMouseenter={() => {
												this.hoveredSeverity = severity.risk;
												this.hoveredProbability = probability.risk;
											}}
											onMouseleave={() => {
												this.hoveredSeverity = null;
												this.hoveredProbability = null;
											}}
										>
											{probability.risk * severity.risk}
										</VCol>
								}}
							></VHover>

						)}
					</VRow>
				)}
			</VCol>;
		},

		genMobileLayout() {
			return <VCol vModel={this.internalValue}>
				<KSelect
					attrs={{
						...this.$props,
						...this.$attrs,
						itemText: "title",
						itemValue: "risk",
						items: this.probabilities,
						label: `Probability${this.required ? "*" : ""}`,
						value: this.internalProbability
					}}
					on={{ input: val => this.internalProbability = val }}
					scopedSlots={{
						item: ( { item } ) =>
							<VListItemContent>
								<VListItemTitle>{item.title}</VListItemTitle>
								<VListItemSubtitle>{item.description}</VListItemSubtitle>
							</VListItemContent>
					}}
				></KSelect>

				<KSelect
					attrs={{
						...this.$props,
						...this.$attrs,
						itemText: "title",
						itemValue: "risk",
						items: this.severities,
						label: `Severity${this.required ? "*" : ""}`,
						value: this.internalSeverity
					}}
					on={{ input: val => this.internalSeverity = val }}
					scopedSlots={{
						item: ( { item } ) =>
							<VListItemContent>
								<VListItemTitle>{item.title}</VListItemTitle>
								<VListItemSubtitle>{item.description}</VListItemSubtitle>
							</VListItemContent>
					}}
				></KSelect>
			</VCol>;
		},

		genKey() {
			return <VCol>
				<VRow class="mt-2">
					<VCol
						class="risk-matrix__cell text-subtitle-1 grey darken-4 white--text"
						cols={12}
					>Key</VCol>

					{this.risksOrder.map( riskKey => {
						const risk = this.risks[riskKey];

						return <VCol
							class={[
								"risk-matrix__cell risk-matrix__cell--selectable",
								risk.classes,
								{
									"risk-matrix__cell--selected": this.internalValue >= risk.rangeMin && this.internalValue <= risk.rangeMax
								}
							]}
							cols={6}
							sm={3}
						>
							<div class="text-body-2">
								{risk.label} Risk ({risk.rangeMin}-{risk.rangeMax})
							</div>

							<div class="text-caption">{risk.action}</div>
						</VCol>;
					} )}
				</VRow>
			</VCol>;
		},

		genLayout() {
			return <div class={{
				"risk-matrix": true,
				"risk-matrix--has-selected": this.internalValue,
				...this.themeClasses
			}}>
				{this.genValidationProvider()}
				{this.genKey}
			</div>;
		}
	},

	methods: {
		genValidatableField() {
			return this.$vuetify.breakpoint.smAndUp
				? this.genDesktopLayout
				: this.genMobileLayout;
		},

		genDefaultSlot() {
			return this.genLayout;
		},

		getRiskClasses( risk ) {
			if ( risk <= this.risks.low.rangeMax ) {
				return this.risks.low.classes;
			} else if ( risk <= this.risks.moderate.rangeMax ) {
				return this.risks.moderate.classes;
			} else if ( risk <= this.risks.high.rangeMax ) {
				return this.risks.high.classes;
			}

			return this.risks.extreme.classes;
		}
	}
};
